import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Badge } from '@thegpvc/ui';
import { formatUsd } from '../utils/format';
import { cents } from '../utils/cents';
import type { HeavySession, UsageResponse } from '../types/api';

// Shown on the continuity tile when the period's model never writes prompt
// cache (#756) — the "N/A" would otherwise be opaque.
const CACHE_NA_TOOLTIP =
  'This period’s model reads prompt cache but never reports writing any, so cache-rebuild (continuity) cost does not apply.';

// Whole-dollar display, e.g. "$1,817".
function usd(dollars: number): string {
  return `$${Math.round(dollars).toLocaleString('en-US')}`;
}

// loop_retry / cache_rebuild → a category tag (§6). Avoidable reads warm,
// structural stays neutral.
function categoryBadge(category?: string): { variant: 'warning' | 'neutral'; label: string } {
  switch (category) {
    case 'loop_retry':
      return { variant: 'warning', label: 'Loop retry' };
    case 'cache_rebuild':
      return { variant: 'neutral', label: 'Cache rebuild' };
    default:
      return { variant: 'neutral', label: category ?? '—' };
  }
}

function pctOfSpend(part: number, spend: number): number {
  return spend > 0 ? Math.round((part / spend) * 100) : 0;
}

function SessionRow({ s, first }: { s: HeavySession; first: boolean }) {
  const badge = categoryBadge(s.category);
  return (
    <Link
      to={`/sessions/${s.id ?? ''}`}
      className="grid items-center gap-3 border-t border-gray-100 px-2 py-2 hover:bg-navy-50 dark:border-navy-800 dark:hover:bg-navy-800"
      style={{
        gridTemplateColumns: 'minmax(0,200px) minmax(0,1fr) auto auto',
        margin: '0 -8px',
        borderTopWidth: first ? 1 : undefined,
      }}
    >
      <span className="truncate font-mono text-xs text-navy-400">{s.project || '—'}</span>
      <span className="truncate text-navy-900 dark:text-white" style={{ fontSize: 13, fontWeight: 500 }}>
        {/* An untitled session falls back to its linked short id, never a bare
            "Session" that reads identically for every one (#737, #748). */}
        {s.title || `Session ${(s.id ?? '').slice(0, 8)}`}
      </span>
      <Badge variant={badge.variant} size="sm">
        {badge.label}
      </Badge>
      <span className="text-right font-mono text-sm tabular-nums text-navy-900 dark:text-white">
        {formatUsd(cents(s.amountCents) / 100)}
      </span>
    </Link>
  );
}

/**
 * OverheadCard (#630 §6) — the overhead ledger. Two equal-weight panels
 * (Avoidable = loop retries, Structural = cache rebuilds), with the combined
 * figure in the card header, then the heaviest overhead sessions as real
 * `<a>` rows for drill-down.
 */
export function OverheadCard({ data }: { data: UsageResponse }) {
  const spend = cents(data.spendCents);
  const avoidable = cents(data.avoidable?.amountCents);
  const structural = cents(data.structural?.amountCents);
  // When the period's model never writes prompt cache (GLM via LunaRoute, #756),
  // continuity is $0 because the metric doesn't apply — not because nothing was
  // wasted. Render "N/A" so the tile can't read as a clean "no cache waste"
  // bill of health, and drop it from the combined figure / % of spend.
  const cacheNA = data.cacheWritesAbsent === true;
  const combined = avoidable + (cacheNA ? 0 : structural);

  const sessions = data.heaviestSessions ?? [];

  return (
    <div className="gp-card gp-card--hairline">
      <div className="mb-4 flex flex-wrap items-baseline justify-between gap-2">
        <div className="flex items-center gap-2">
          <span
            aria-hidden
            className="inline-block rounded-full"
            style={{ width: 6, height: 6, background: 'var(--color-orange-500)' }}
          />
          <span className="text-xs font-semibold uppercase tracking-wider text-orange-600">
            Overhead
          </span>
        </div>
        {/* Combined figure lives in the header (§6). */}
        <div className="font-mono text-sm tabular-nums">
          <span className="text-navy-900 dark:text-white">{usd(combined / 100)}</span>
          <span className="ml-1 text-navy-400"> · {pctOfSpend(combined, spend)}% of spend</span>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {/* Avoidable — orange-tinted, recoverable. */}
        <div className="rounded-md border border-orange-200 bg-orange-50 p-4">
          <div className="text-xs font-semibold uppercase tracking-wider text-orange-700">
            Loops &amp; re-reads
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <span
              className="font-mono font-semibold tabular-nums text-navy-900"
              style={{ fontSize: 26 }}
            >
              {usd(avoidable / 100)}
            </span>
            <span className="text-sm text-navy-400">{pctOfSpend(avoidable, spend)}% of spend</span>
          </div>
          <p className="mt-2 text-sm text-navy-400">
            Same-tool flail runs and repeat file reads inside one session.
          </p>
          <Link
            to="/sessions"
            className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-orange-600"
          >
            View sessions <ArrowRight size={12} aria-hidden />
          </Link>
        </div>

        {/* Structural — sunken/neutral, the cost of continuity (not recoverable). */}
        <div className="rounded-md border border-gray-200 bg-gray-50 p-4 dark:bg-navy-800">
          <div className="text-xs font-semibold uppercase tracking-wider text-navy-400">
            Cost of continuity
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <span
              className="font-mono font-semibold tabular-nums text-navy-900 dark:text-white"
              style={{ fontSize: 26 }}
              title={cacheNA ? CACHE_NA_TOOLTIP : undefined}
            >
              {cacheNA ? 'N/A' : usd(structural / 100)}
            </span>
            {!cacheNA && (
              <span className="text-sm text-navy-400">
                {pctOfSpend(structural, spend)}% of spend
              </span>
            )}
          </div>
          {cacheNA ? (
            <p className="mt-2 text-sm text-navy-400">
              This period’s model reads prompt cache but never reports writing any,
              so continuity cost does not apply.
            </p>
          ) : (
            <>
              <p className="mt-2 text-sm text-navy-400">
                Prompt cache rebuilt when you resume a session after 30+ minutes away.
              </p>
              {/* fg-4 (navy-400) clears AA for this informative footer (§6). */}
              <p className="mt-2 text-xs text-navy-400">
                Not recoverable — a fresh session rebuilds the same context.
              </p>
            </>
          )}
        </div>
      </div>

      {sessions.length > 0 && (
        <div className="mt-4">
          <div className="mb-1 text-xs font-medium uppercase tracking-wider text-navy-400">
            Heaviest sessions
          </div>
          {sessions.map((s, i) => (
            <SessionRow key={s.id ?? i} s={s} first={i === 0} />
          ))}
        </div>
      )}
    </div>
  );
}
